import "twin.macro";

import { useAuth0 } from "@auth0/auth0-react";
import { t, TFunction } from "i18next";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  BatteryFullIcon,
  BatteryLowIcon,
  BatteryMediumIcon,
  BatteryWarningIcon,
  CirclePause,
  Clock3Icon,
  Loader2,
  LockKeyhole,
  LucideIcon,
  MoveDown,
  MoveLeft,
  MoveRight,
  MoveUp,
  Pause,
  Power,
  RefreshCcw,
  RotateCcw,
  ShieldQuestionIcon,
  X,
} from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import tw from "twin.macro";
import { useMediaQuery } from "usehooks-ts";

import { ModuleCommandEnum } from "@/api/events/modules/command.enum";
import { CommandTrackingView } from "@/api/events/modules/command-tracking";
import { InferredStateEnum } from "@/api/events/modules/inferred-transition-state.enum";
import { isLockedState } from "@/api/events/modules/isLockedState";
import { isPausedState } from "@/api/events/modules/isPausedStateEnum";
import { ModuleStateEnum } from "@/api/events/modules/module-state.enum";
import { ModuleTrackingView } from "@/api/events/modules/module-tracking";
import { useSendCommand } from "@/api/modules/mutations";
import OpenLockIcon from "@/components/open-lock";
import { useAreaContext } from "@/hooks/useAreaContext";
import { usePostDetailContext } from "@/hooks/usePostDetailContext";

import BottomSheetErrorList from "./bottom-sheet-error-list";
import ErrorListModal from "./error-list-modal";
import Skeleton from "./skeleton";

type ActionButtonProps = {
  isDisabled: boolean;
  iconLeft?: LucideIcon;
  iconRight?: LucideIcon;
  translationKey: "up" | "down" | "open" | "close" | "pause" | "reset";
  action: ModuleCommandEnum;
  postName: string;
  onAction: (postName: string, action: ModuleCommandEnum) => void;
};

type TransitionSnapshot = {
  isModuleLockedState: boolean;
  inferredState: InferredStateEnum;
  isRefreshing: boolean;
  isPending: boolean;
};

/** once omis ou true : un seul déclenchement puis retrait. once: false : abonnement récurrent. */
type TransitionSubscription = {
  shouldTrigger: (
    previous: TransitionSnapshot,
    current: TransitionSnapshot
  ) => boolean;
  onTransition: () => void;
  once?: boolean;
};

const ActionButton = ({
  iconLeft: IconLeft,
  iconRight: IconRight,
  translationKey,
  action,
  postName,
  isDisabled,
  onAction,
}: ActionButtonProps) => {
  return (
    <button
      onClick={() => onAction(postName, action)}
      disabled={isDisabled}
      css={[
        tw`flex-1 h-12 text-gray-600 bg-white border border-gray-300 rounded-lg flex items-center justify-center gap-2 hover:border-gray-700 hover:text-gray-900 transition-colors font-semibold`,
        tw`active:bg-gray-200`,
        tw`active:p-2`,
        tw`rounded-lg`,
        tw`active:scale-95`,
        tw`disabled:opacity-50 disabled:cursor-not-allowed`,
      ]}
    >
      {IconLeft && <IconLeft tw="w-5 h-5" />}
      <span>{t(`postMaintenance.actions.${translationKey}`)}</span>
      {IconRight && <IconRight tw="w-5 h-5" />}
    </button>
  );
};

const getModuleImageName = (moduleState: InferredStateEnum) => {
  switch (moduleState) {
    case InferredStateEnum.FOLDED:
      return "folded";
    case InferredStateEnum.UP:
      return "up";
    case InferredStateEnum.UNFOLDED:
      return "unfolded";
    case InferredStateEnum.CLOSING:
      return "unfolded";
    case InferredStateEnum.OPENING:
      return "unfolded";
    case InferredStateEnum.PROGRESS_UP:
      return "up";
    case InferredStateEnum.PROGRESS_DOWN:
      return "folded";
    default:
      return "default";
  }
};

const getLedState = (moduleState: InferredStateEnum) => {
  switch (moduleState) {
    case InferredStateEnum.FOLDED:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-folded text-modulesLeds-folded" />
      );
    case InferredStateEnum.UP:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-up text-modulesLeds-up" />
      );
    case InferredStateEnum.UNFOLDED:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-unfolded text-modulesLeds-unfolded" />
      );
    case InferredStateEnum.ERROR:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-error text-modulesLeds-error" />
      );
    case InferredStateEnum.LOCKED:
      return <div tw="rounded-full w-3 h-3 bg-gray-500 text-gray-500" />;
    case InferredStateEnum.WAITING_FOR_STATE:
    case InferredStateEnum.UNFOLDING:
    case InferredStateEnum.FOLDING:
    case InferredStateEnum.OPENING:
    case InferredStateEnum.CLOSING:
    case InferredStateEnum.PROGRESS_UP:
    case InferredStateEnum.PROGRESS_DOWN:
    case InferredStateEnum.PAUSED:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-inProgress text-modulesLeds-inProgress" />
      );
    case InferredStateEnum.UNKNOWN:
      return <div tw="rounded-full w-3 h-3 bg-gray-500 text-gray-500" />;
  }
};

const getStateTranslation = (
  moduleState: InferredStateEnum,
  translator: TFunction<"translation", undefined>
) => {
  switch (moduleState) {
    case InferredStateEnum.FOLDED:
      return translator("postMaintenance.state.FOLDED");
    case InferredStateEnum.UP:
      return translator("postMaintenance.state.UP");
    case InferredStateEnum.UNFOLDED:
      return translator("postMaintenance.state.UNFOLDED");
    case InferredStateEnum.ERROR:
      return translator("postMaintenance.state.ERROR");
    case InferredStateEnum.WAITING_FOR_STATE:
      return translator("postMaintenance.state.WAITING_FOR_STATE");
    case InferredStateEnum.PAUSED:
      return translator("postMaintenance.state.PAUSED");
    case InferredStateEnum.UNFOLDING:
      return translator("postMaintenance.state.UNFOLDING");
    case InferredStateEnum.FOLDING:
      return translator("postMaintenance.state.FOLDING");
    case InferredStateEnum.OPENING:
      return translator("postMaintenance.state.OPENING");
    case InferredStateEnum.CLOSING:
      return translator("postMaintenance.state.CLOSING");
    case InferredStateEnum.PROGRESS_UP:
      return translator("postMaintenance.state.PROGRESS_UP");
    case InferredStateEnum.PROGRESS_DOWN:
      return translator("postMaintenance.state.PROGRESS_DOWN");
    case InferredStateEnum.UNKNOWN:
      return translator("postMaintenance.state.UNKNOWN");
  }
};

const getStateOfChargeIcon = (stateOfCharge: number | undefined) => {
  if (!stateOfCharge) {
    return <BatteryWarningIcon tw="w-5 h-5" color="gray" />;
  }

  if (stateOfCharge && stateOfCharge < 30) {
    return <BatteryLowIcon tw="w-5 h-5" color="red" />;
  } else if (stateOfCharge && stateOfCharge > 60) {
    return <BatteryFullIcon tw="w-5 h-5" color="green" />;
  } else {
    return <BatteryMediumIcon tw="w-5 h-5" color="orange" />;
  }
};

const getAnimationByState = (
  state: InferredStateEnum,
  moduleState: ModuleStateEnum
) => {
  if (isLockedState(moduleState)) {
    return (
      <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
        <LockKeyhole tw="w-full h-full text-gray-500/70 opacity-100" />
      </div>
    );
  }

  if (isPausedState(moduleState)) {
    return (
      <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
        <CirclePause tw="w-full h-full text-gray-500/70 opacity-100" />
      </div>
    );
  }

  switch (state) {
    case InferredStateEnum.UNFOLDING:
      return (
        <div>
          <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
            <MoveLeft
              tw="w-full h-full text-blue-500/70 animate-blink"
              strokeWidth={0.75}
            />
            <MoveRight
              tw="w-full h-full text-blue-500/70 animate-blink"
              strokeWidth={0.75}
            />
          </div>
          <div tw="flex items-center absolute inset-0 w-full h-full pt-20">
            <MoveUp
              tw="w-full h-2/3 text-blue-500/70 animate-blink"
              strokeWidth={0.75}
            />
          </div>
        </div>
      );
    case InferredStateEnum.FOLDING:
      return (
        <div>
          <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
            <MoveRight
              tw="w-full h-full text-blue-500/70 animate-blink"
              strokeWidth={0.75}
            />
            <MoveLeft
              tw="w-full h-full text-blue-500/70 animate-blink"
              strokeWidth={0.75}
            />
          </div>
          <div tw="flex items-center absolute inset-0 w-full h-full pt-20">
            <MoveDown
              tw="w-full h-2/3 text-blue-500/70 animate-blink"
              strokeWidth={0.75}
            />
          </div>
        </div>
      );
    case InferredStateEnum.OPENING:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <MoveLeft
            tw="w-full h-full text-blue-500/70 animate-blink"
            strokeWidth={0.75}
          />
          <MoveRight
            tw="w-full h-full text-blue-500/70 animate-blink"
            strokeWidth={0.75}
          />
        </div>
      );
    case InferredStateEnum.CLOSING:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <MoveRight
            tw="w-full h-full text-blue-500/70 animate-blink"
            strokeWidth={0.75}
          />
          <MoveLeft
            tw="w-full h-full text-blue-500/70 animate-blink"
            strokeWidth={0.75}
          />
        </div>
      );
    case InferredStateEnum.PROGRESS_UP:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <MoveUp
            tw="w-full h-full text-blue-500/70 animate-blink"
            strokeWidth={0.75}
          />
        </div>
      );
    case InferredStateEnum.PROGRESS_DOWN:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <MoveDown
            tw="w-full h-full text-blue-500/70 animate-blink"
            strokeWidth={0.75}
          />
        </div>
      );
    case InferredStateEnum.ERROR:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <X tw="w-full h-full text-red-500/70" strokeWidth={0.75} />
        </div>
      );
    case InferredStateEnum.WAITING_FOR_STATE:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <Loader2 tw="w-full h-full text-gray-500/70 animate-spin" />
        </div>
      );
    case InferredStateEnum.UNKNOWN:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <ShieldQuestionIcon
            tw="w-full h-full text-gray-500/70"
            strokeWidth={0.75}
          />
        </div>
      );
  }
};

const isModuleInProgress = (machineState: InferredStateEnum) => {
  return [
    InferredStateEnum.UNFOLDING,
    InferredStateEnum.FOLDING,
    InferredStateEnum.OPENING,
    InferredStateEnum.CLOSING,
    InferredStateEnum.PROGRESS_UP,
    InferredStateEnum.PROGRESS_DOWN,
  ].includes(machineState);
};

const getInferredStateFromMachineState = (
  machineState: ModuleStateEnum,
  lastCommand: CommandTrackingView
): InferredStateEnum => {
  switch (machineState) {
    case ModuleStateEnum.FOLDED:
      return InferredStateEnum.FOLDED;
    case ModuleStateEnum.UP:
      return InferredStateEnum.UP;
    case ModuleStateEnum.UNFOLDED:
      return InferredStateEnum.UNFOLDED;
    case ModuleStateEnum.FOLDED_STEP_1:
    case ModuleStateEnum.FOLDED_STEP_2:
    case ModuleStateEnum.FOLDED_STEP_3:
    case ModuleStateEnum.FOLDED_STEP_4:
    case ModuleStateEnum.FOLDED_STEP_5:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.UNFOLDED_STEP_1:
    case ModuleStateEnum.UNFOLDED_STEP_2:
    case ModuleStateEnum.UNFOLDED_STEP_3:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.PAUSED_FOLDED:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.PAUSED_UP:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.PAUSED_UNFOLDED:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.PAUSED_UNFOLDED_STEP_1:
    case ModuleStateEnum.PAUSED_UNFOLDED_STEP_2:
    case ModuleStateEnum.PAUSED_UNFOLDED_STEP_3:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.PAUSED_FOLDED_STEP_1:
    case ModuleStateEnum.PAUSED_FOLDED_STEP_2:
    case ModuleStateEnum.PAUSED_FOLDED_STEP_3:
    case ModuleStateEnum.PAUSED_FOLDED_STEP_4:
    case ModuleStateEnum.PAUSED_FOLDED_STEP_5:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.LOCKED_FOLDED:
      return InferredStateEnum.FOLDED;
    case ModuleStateEnum.LOCKED_UP:
      return InferredStateEnum.UP;
    case ModuleStateEnum.LOCKED_UNFOLDED:
      return InferredStateEnum.UNFOLDED;
    case ModuleStateEnum.LOCKED_UNFOLDED_STEP_1:
    case ModuleStateEnum.LOCKED_UNFOLDED_STEP_2:
    case ModuleStateEnum.LOCKED_UNFOLDED_STEP_3:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.LOCKED_FOLDED_STEP_1:
    case ModuleStateEnum.LOCKED_FOLDED_STEP_2:
    case ModuleStateEnum.LOCKED_FOLDED_STEP_3:
    case ModuleStateEnum.LOCKED_FOLDED_STEP_4:
    case ModuleStateEnum.LOCKED_FOLDED_STEP_5:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED:
      return InferredStateEnum.FOLDED;
    case ModuleStateEnum.LOCKED_PAUSED_UP:
      return InferredStateEnum.UP;
    case ModuleStateEnum.LOCKED_PAUSED_UNFOLDED:
      return InferredStateEnum.UNFOLDED;
    case ModuleStateEnum.LOCKED_PAUSED_UNFOLDED_STEP_1:
    case ModuleStateEnum.LOCKED_PAUSED_UNFOLDED_STEP_2:
    case ModuleStateEnum.LOCKED_PAUSED_UNFOLDED_STEP_3:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_1:
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_2:
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_3:
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_4:
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_5:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.ERROR:
      return InferredStateEnum.ERROR;
    case ModuleStateEnum.WAITING_FOR_STATE:
      return getInferredStateFromCommand(lastCommand);
    default:
      return InferredStateEnum.UNKNOWN;
  }
};

const getInferredStateFromCommand = (
  lastCommand: CommandTrackingView
): InferredStateEnum => {
  if (lastCommand.command === ModuleCommandEnum.UNFOLD) {
    return InferredStateEnum.UNFOLDING;
  }
  if (lastCommand.command === ModuleCommandEnum.FOLD) {
    return InferredStateEnum.FOLDING;
  }
  if (lastCommand.command === ModuleCommandEnum.OPEN) {
    return InferredStateEnum.OPENING;
  }
  if (lastCommand.command === ModuleCommandEnum.CLOSE) {
    return InferredStateEnum.CLOSING;
  }
  if (lastCommand.command === ModuleCommandEnum.UP) {
    return InferredStateEnum.PROGRESS_UP;
  }
  if (lastCommand.command === ModuleCommandEnum.DOWN) {
    return InferredStateEnum.PROGRESS_DOWN;
  }
  return InferredStateEnum.WAITING_FOR_STATE;
};

const getInferredState = (
  machineState: ModuleStateEnum,
  lastCommand: CommandTrackingView
): InferredStateEnum => {
  return getInferredStateFromMachineState(machineState, lastCommand);
};

export function PostMaintenance() {
  const { getAccessTokenSilently } = useAuth0();

  const { post } = usePostDetailContext();
  const { area } = useAreaContext();
  const { mutate: sendCommand, isPending } = useSendCommand({
    areaId: area._id,
  });

  const { t: translator } = useTranslation();

  const [isErrorListOpened, setIsErrorListOpened] = useState(false);
  const [moduleTrackingState, setModuleTrackingState] =
    useState<ModuleTrackingView>();
  const [commandTrackingState, setCommandTrackingState] =
    useState<CommandTrackingView>();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [inferredState, setInferredState] = useState<InferredStateEnum>(
    InferredStateEnum.WAITING_FOR_STATE
  );
  const [isModuleLockedState, setIsModuleLockedState] = useState(false);
  const previousTransitionSnapshot = useRef<TransitionSnapshot>({
    isModuleLockedState: false,
    inferredState: InferredStateEnum.WAITING_FOR_STATE,
    isRefreshing: false,
    isPending: false,
  });
  const transitionSubscriptions = useRef<TransitionSubscription[]>([]);

  const isDesktop = useMediaQuery("(min-width: 822px)");

  const handleAction = (moduleAddress: string, action: ModuleCommandEnum) => {
    setIsRefreshing(true);
    console.log("Action envoyée :", action);
    sendCommand({ destinationAddress: moduleAddress, command: action });
  };

  const subscribeToTransition = (subscription: TransitionSubscription) => {
    transitionSubscriptions.current.push(subscription);
  };

  useEffect(() => {
    if (!commandTrackingState || !moduleTrackingState) {
      setIsRefreshing(true);
      return;
    }
    const inferredState = getInferredState(
      moduleTrackingState.machineState,
      commandTrackingState
    );
    setInferredState(inferredState);
    setIsModuleLockedState(isLockedState(moduleTrackingState.machineState));

    if (isModuleInProgress(inferredState)) {
      setIsRefreshing(true);
    } else {
      setIsRefreshing(false);
    }

    return;
  }, [commandTrackingState, moduleTrackingState]);

  useEffect(() => {
    const currentSnapshot: TransitionSnapshot = {
      isModuleLockedState,
      inferredState,
      isRefreshing,
      isPending,
    };
    const previousSnapshot = previousTransitionSnapshot.current;

    const remaining: TransitionSubscription[] = [];
    for (const subscription of transitionSubscriptions.current) {
      const shouldRun = subscription.shouldTrigger(
        previousSnapshot,
        currentSnapshot
      );

      if (shouldRun) {
        subscription.onTransition();
        if (subscription.once === false) {
          remaining.push(subscription);
        }
      } else {
        remaining.push(subscription);
      }
    }

    transitionSubscriptions.current = remaining;
    previousTransitionSnapshot.current = currentSnapshot;
  }, [isModuleLockedState, inferredState, isRefreshing, isPending]);

  useEffect(() => {
    let source: EventSource | null = null;

    getAccessTokenSilently().then((token) => {
      source = new EventSource(
        `${import.meta.env.VITE_API_URL}/module-interactions/subscribe-notifications/area/${area._id}/module/${post.address.toString()}?token=${token}`
      );

      source.onopen = () => {
        console.log("Connecté au serveur");
      };

      source.addEventListener("command-tracking", (e) => {
        const payload = JSON.parse(e.data) as CommandTrackingView;
        console.log(payload);
        setCommandTrackingState(payload);
      });

      source.addEventListener("module-state-tracking", (e) => {
        const payload = JSON.parse(e.data) as ModuleTrackingView;
        console.log(payload);
        setModuleTrackingState(payload);
      });

      source.onerror = (err) => {
        console.error("Erreur SSE :", err);
        setIsRefreshing(false);
      };
    });

    return () => {
      if (source) {
        console.log("Closing SSE");
        source.close();
      }
    };
  }, [getAccessTokenSilently]);

  // Squelette de chargement
  if (!moduleTrackingState?.machineState || !commandTrackingState?.command) {
    return <Skeleton />;
  }

  // Maquette normale
  return (
    <div tw="bg-white shadow rounded-xl max-w-xl flex flex-col items-center gap-6 p-2 md:p-6 pt-2">
      <div>
        <div tw="flex flex-col bg-white items-center justify-between w-full gap-4">
          <div tw="flex items-center gap-8 bg-gray-50 rounded-lg shadow p-2">
            <div tw="flex items-center gap-2">
              <span>{getLedState(inferredState)}</span>
              <span tw="text-gray-600 font-normal text-sm">
                {getStateTranslation(inferredState, translator)}
              </span>
              <span>-</span>
              <span tw="text-gray-600 font-normal text-sm">
                {moduleTrackingState.machineState}
              </span>
            </div>
            <div tw="flex items-center gap-2">
              <span>
                {getStateOfChargeIcon(moduleTrackingState.stateOfCharge)}
              </span>
              <span tw="text-gray-600 font-normal text-sm">
                {moduleTrackingState.stateOfCharge
                  ? `${moduleTrackingState.stateOfCharge}%`
                  : "❔"}
              </span>
            </div>
          </div>
          <p tw="flex items-center gap-2 text-gray-500 font-normal text-sm">
            <Clock3Icon tw="w-5 h-5" />
            <span tw="text-gray-500 font-normal text-sm">
              Status du{" "}
              {DateTime.fromISO(moduleTrackingState.createdAt)
                .setLocale("fr")
                .toFormat(
                  `'${t("postMaintenance.time.statusSince")}' dd MMMM yyyy '${t("postMaintenance.time.statusAt")}' HH'h'mm`
                )}
            </span>
          </p>
          <div
            css={[
              tw`grid grid-cols-3 w-full h-12 rounded-2xl border border-[rgb(215,226,240)] bg-[rgb(247,251,255)] shadow-sm`,
            ]}
          >
            <button
              onClick={() =>
                handleAction(
                  post.address.toString(),
                  ModuleCommandEnum.GET_STATUS
                )
              }
              disabled={isPending}
              aria-label={t("postMaintenance.actions.refresh")}
              title={t("postMaintenance.actions.refresh")}
              css={[
                tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
                tw`hover:bg-slate-50 hover:text-slate-900 hover:rounded-l-2xl`,
                tw`active:scale-[0.98] active:bg-slate-100`,
                tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                tw`border-r border-[rgb(215,226,240)]`,
              ]}
            >
              <RefreshCcw
                tw="w-5 h-5 shrink-0"
                css={isRefreshing && tw`animate-spin`}
              />

              <span
                css={[
                  tw`hidden truncate`,
                  { "@media (min-width: 515px)": { display: "inline" } },
                ]}
              >
                {t("postMaintenance.actions.refresh")}
              </span>
            </button>

            <button
              onClick={() =>
                handleAction(post.address.toString(), ModuleCommandEnum.REBOOT)
              }
              disabled={isPending}
              aria-label={t("postMaintenance.actions.reboot")}
              title={t("postMaintenance.actions.reboot")}
              css={[
                tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
                tw`hover:bg-slate-50 hover:text-slate-900`,
                tw`active:scale-[0.98] active:bg-slate-100`,
                tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                tw`border-r border-[rgb(215,226,240)]`,
              ]}
            >
              <Power tw="w-5 h-5 shrink-0" />

              <span
                css={[
                  tw`hidden truncate`,
                  { "@media (min-width: 515px)": { display: "inline" } },
                ]}
              >
                {t("postMaintenance.actions.reboot")}
              </span>
            </button>

            <button
              onClick={() => {
                subscribeToTransition({
                  shouldTrigger: (previous, current) =>
                    !previous.isModuleLockedState &&
                    current.isModuleLockedState,
                  onTransition: () =>
                    toast.success(translator("action.successLockingModule")),
                });
                handleAction(post.address.toString(), ModuleCommandEnum.LOCK);
              }}
              disabled={isPending}
              aria-label="Maintenance"
              title="Maintenance"
              css={[
                tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
                tw`hover:bg-slate-50 hover:text-slate-900 hover:rounded-2xl`,
                tw`active:scale-[0.98] active:bg-slate-100`,
                tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                isModuleLockedState && tw`hidden`,
              ]}
            >
              <LockKeyhole tw="w-5 h-5 shrink-0" />

              <span
                css={[
                  tw`hidden truncate`,
                  { "@media (min-width: 515px)": { display: "inline" } },
                ]}
              >
                Maintenance
              </span>
            </button>

            <button
              onClick={() => {
                subscribeToTransition({
                  shouldTrigger: (previous, current) =>
                    previous.isModuleLockedState &&
                    !current.isModuleLockedState,
                  onTransition: () =>
                    toast.success(translator("action.successUnlockingModule")),
                });
                handleAction(post.address.toString(), ModuleCommandEnum.UNLOCK);
              }}
              disabled={isPending}
              aria-label="Maintenance"
              title="Maintenance"
              css={[
                tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
                tw`hover:bg-slate-50 hover:text-slate-900 hover:rounded-2xl`,
                tw`active:scale-[0.98] active:bg-slate-100`,
                tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                !isModuleLockedState && tw`hidden`,
              ]}
            >
              <OpenLockIcon tw="w-5 h-5 shrink-0" />

              <span
                css={[
                  tw`hidden truncate`,
                  { "@media (min-width: 515px)": { display: "inline" } },
                ]}
              >
                Fin de maintenance
              </span>
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setIsErrorListOpened(true)}
        tw="flex items-center justify-center bg-red-500/80 rounded-md p-2 hidden"
        css={
          moduleTrackingState.machineErrors.length ||
          (moduleTrackingState.applicationErrors.length > 0 && tw`block`)
        }
      >
        <p tw="text-white text-sm">
          <span tw="">Défauts à traiter</span>{" "}
          <span tw="text-white text-sm">→</span>
        </p>
      </button>

      {/* Icône d'Pantographe */}
      <div
        tw="flex items-center justify-center bg-greenFoundCanopee rounded-xl p-2 w-full max-w-80 h-60 relative"
        css={
          inferredState === InferredStateEnum.ERROR &&
          tw`border-2 border-red-500`
        }
      >
        <img
          src={`/posts/${getModuleImageName(inferredState)}.svg`}
          alt="Pantograph"
          tw="w-60 h-48"
        />

        {getAnimationByState(inferredState, moduleTrackingState.machineState)}
      </div>

      {/* Première rangée de boutons */}
      <div tw="flex gap-4 w-full">
        <ActionButton
          isDisabled={isRefreshing || isPending || isModuleLockedState}
          iconLeft={ArrowUp}
          translationKey="up"
          action={ModuleCommandEnum.UP}
          postName={post.address.toString()}
          onAction={handleAction}
        />
        <ActionButton
          isDisabled={isRefreshing || isPending || isModuleLockedState}
          iconLeft={ArrowDown}
          translationKey="down"
          action={ModuleCommandEnum.DOWN}
          postName={post.address.toString()}
          onAction={handleAction}
        />
      </div>

      {/* Deuxième rangée de boutons */}
      <div tw="flex gap-4 w-full">
        <ActionButton
          isDisabled={isRefreshing || isPending || isModuleLockedState}
          iconLeft={ArrowLeft}
          iconRight={ArrowRight}
          translationKey="open"
          action={ModuleCommandEnum.OPEN}
          postName={post.address.toString()}
          onAction={handleAction}
        />
        <ActionButton
          isDisabled={isRefreshing || isPending || isModuleLockedState}
          iconLeft={ArrowRight}
          iconRight={ArrowLeft}
          translationKey="close"
          action={ModuleCommandEnum.CLOSE}
          postName={post.address.toString()}
          onAction={handleAction}
        />
      </div>

      {/* Bouton Déployer */}
      <button
        onClick={() =>
          handleAction(post.address.toString(), ModuleCommandEnum.UNFOLD)
        }
        css={[
          tw`w-full h-12 shadow-sm bg-secondary-600 hover:bg-secondary-700 focus:(ring-secondary-600) text-white rounded-xl font-semibold transition-colors font-semibold`,
          tw`active:bg-secondary-700`,
          tw`active:scale-95`,
          tw`disabled:opacity-50 disabled:cursor-not-allowed`,
        ]}
        disabled={
          isRefreshing ||
          ModuleStateEnum.UNFOLDED === moduleTrackingState.machineState ||
          isPending ||
          isModuleLockedState
        }
      >
        {t("postMaintenance.actions.unfold")}
      </button>

      {/* Bouton Replier */}
      <button
        onClick={() =>
          handleAction(post.address.toString(), ModuleCommandEnum.FOLD)
        }
        css={[
          tw`w-full h-12 shadow-sm bg-secondary-600 hover:bg-secondary-700 focus:(ring-secondary-600) text-white rounded-xl font-semibold transition-colors font-semibold`,
          tw`active:bg-secondary-700`,
          tw`active:scale-95`,
          tw`disabled:opacity-50 disabled:cursor-not-allowed`,
        ]}
        disabled={
          isRefreshing ||
          ModuleStateEnum.FOLDED === moduleTrackingState.machineState ||
          isPending ||
          isModuleLockedState
        }
      >
        {t("postMaintenance.actions.fold")}
      </button>

      {/* Troisième rangée de boutons */}
      <div tw="flex gap-4 w-full">
        <ActionButton
          iconLeft={Pause}
          translationKey="pause"
          action={ModuleCommandEnum.PAUSE}
          postName={post.address.toString()}
          onAction={handleAction}
          isDisabled={isPending || isModuleLockedState}
        />
        <ActionButton
          isDisabled={isPending || isModuleLockedState}
          iconLeft={RotateCcw}
          translationKey="reset"
          action={ModuleCommandEnum.RESET}
          postName={post.address.toString()}
          onAction={handleAction}
        />
      </div>

      {isDesktop ? (
        <ErrorListModal
          areaId={area._id}
          open={isErrorListOpened}
          setOpen={setIsErrorListOpened}
          errors={[
            ...moduleTrackingState.machineErrors,
            ...moduleTrackingState.applicationErrors,
          ]}
          moduleAddress={post.address.toString()}
        />
      ) : (
        <BottomSheetErrorList
          areaId={area._id}
          open={isErrorListOpened}
          setOpen={setIsErrorListOpened}
          errors={[
            ...moduleTrackingState.machineErrors,
            ...moduleTrackingState.applicationErrors,
          ]}
          moduleAddress={post.address.toString()}
        />
      )}
    </div>
  );
}
